import { useEffect, useRef, useState } from 'react';
import { gsap } from '../../motion/gsap';
import { shouldAnimate } from '../../lib/format';
import { Check, Copy, Send, Users } from 'lucide-react';
import { api, avatarUrl } from '../../lib/api';
import { toast, useSession } from '../../lib/session';
import { useRoomSync } from '../../sync/roomStore';
import type { TeamMember } from '../../lib/types';
import { Avatar, Modal, ModalTitle } from '../../ui';
import { useT } from '../../lib/i18n';

/**
 * Invite team members into this room directly: they get a popup in the app with Join. The room
 * link stays below for anyone who isn't looking at the app.
 */
export function InviteDialog({ roomId, roomName, onClose }: { roomId?: string; roomName: string; onClose: () => void }) {
  const t = useT();
  const me = useSession((s) => s.user);
  // Who's inside: known only for the room this device is in.
  const peers = useRoomSync((s) => (s.roomId === roomId ? s.peers : []));
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [sent, setSent] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    api.team().then((tm) => setMembers(tm.members ?? []), () => setMembers([]));
  }, []);
  const here = new Set(peers.map((p) => p.userId));
  const others = (members ?? [])
    .filter((m) => m.id !== me?.id)
    .sort((a, b) => Number(here.has(a.id)) - Number(here.has(b.id)) || Number(b.online) - Number(a.online) || a.name.localeCompare(b.name));
  const invitable = others.filter((m) => !here.has(m.id) && !sent.has(m.id));

  const invite = async (ids: string[], key: string) => {
    if (!roomId || !ids.length) return;
    setBusy(key);
    try {
      const r = await api.inviteToRoom(roomId, ids);
      setSent((s) => new Set([...s, ...r.invited]));
      if (r.invited.length) toast(r.invited.length === 1 ? t('invite.sentOne') : t('invite.sentMany', { n: r.invited.length }), 'accent');
    } catch (e) {
      toast((e as Error).message, 'red');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal open onClose={onClose}>
      <ModalTitle>{t('invite.title', { room: roomName })}</ModalTitle>
      <p className="muted" style={{ margin: '0 0 14px', fontSize: 13 }}>
        {t('invite.sub')}
      </p>
      {members === null ? (
        <p className="muted">…</p>
      ) : !others.length ? (
        <p className="muted" style={{ fontSize: 13 }}>
          {t('invite.nobody')}
        </p>
      ) : (
        <>
          <div className="invite-members">
            {others.map((m) => {
              const inside = here.has(m.id);
              const done = sent.has(m.id);
              return (
                <div className="invite-member" key={m.id}>
                  <Avatar name={m.name} src={avatarUrl(m)} online={m.online} />
                  <div className="invite-member-who">
                    <b className="truncate">{m.name}</b>
                    <span className={m.online ? 'on' : ''}>{inside ? t('invite.inside') : m.online ? t('team.online') : t('invite.offline')}</span>
                  </div>
                  {inside ? (
                    <span className="chip chip-gray">{t('invite.here')}</span>
                  ) : done ? (
                    <span className="chip chip-accent">
                      <Check size={12} /> {t('invite.sent')}
                    </span>
                  ) : (
                    <button className="btn btn-black btn-sm" disabled={busy !== null} onClick={() => invite([m.id], m.id)}>
                      <Send size={13} /> {t('btn.invite')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {invitable.length > 1 && (
            <button className="btn btn-accent" style={{ width: '100%', marginTop: 10 }} disabled={busy !== null} onClick={() => invite(invitable.map((m) => m.id), 'all')}>
              <Users size={15} /> {t('invite.everyone', { n: invitable.length })}
            </button>
          )}
        </>
      )}
      <RoomLink roomId={roomId} />
    </Modal>
  );
}

/** The plain room link, for sending through another app. */
function RoomLink({ roomId }: { roomId?: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const url = `${location.origin}/room/${roomId}`;
  const btn = useRef<HTMLButtonElement>(null);
  const firstRender = useRef(true);
  // The button morphs between "Copy link" and "Copied": its width tweens and the icon pops.
  useEffect(() => {
    if (firstRender.current) return void (firstRender.current = false);
    const el = btn.current;
    if (!el || !shouldAnimate()) return;
    const from = el.dataset.w ? Number(el.dataset.w) : el.offsetWidth;
    el.style.width = '';
    const to = el.offsetWidth;
    el.dataset.w = String(to);
    gsap.fromTo(el, { width: from }, { width: to, duration: 0.35, ease: 'power3.out', clearProps: 'width' });
    gsap.fromTo(el.querySelector('svg'), { scale: 0.4, rotate: -30 }, { scale: 1, rotate: 0, duration: 0.4, ease: 'back.out(2.4)' });
  }, [copied]);
  return (
    <details className="invite-link">
      <summary>{t('invite.orLink')}</summary>
      <div className="row" style={{ marginTop: 10 }}>
        <input className="input mono" readOnly value={url} onFocus={(e) => e.target.select()} style={{ fontSize: 12 }} aria-label={t('room.linkField')} />
        <button
          ref={btn}
          type="button"
          className={`btn btn-black copy-btn${copied ? ' copied' : ''}`}
          onClick={async () => {
            if (btn.current) btn.current.dataset.w = String(btn.current.offsetWidth);
            await navigator.clipboard.writeText(url).catch(() => undefined);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
          }}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
    </details>
  );
}
